// „Мария вече брои Бар — продължете нейното броене или започнете отделно?" —
// въпросът при „Започни" / „+ Нова инвентаризация" за склад, който КОЛЕГА брои.
// Потвърждение, не забрана: двете броения се записват като отделни
// инвентаризации. Без колега на склада — направо (`proceed`). Моята незавършена
// на същия склад не пита тук — за нея е въпросът „Продължи / Изтрий и започни
// нова" на `useHomeDrafts`.
import { useState } from "react";
import dayjs from "dayjs";
import { TeamOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import type { DraftSummary } from "../../../components/drafts/draftModel";
import { ConfirmDialog } from "../../../components/confirm/ConfirmDialog";
import type { WarehouseUnfinished } from "./StocktakeHomeSections";

type Asking = { draft: DraftSummary; warehouseName: string; proceed: () => void };

type Params = {
  unfinishedOf: (warehouseId: number) => WarehouseUnfinished[];
  warehouseNameOf: (warehouseId: number) => string;
};

export const useColleagueCountQuestion = ({ unfinishedOf, warehouseNameOf }: Params) => {
  const { t } = useTranslation();
  const [asking, setAsking] = useState<Asking | null>(null);

  // warehouseId null — листът тръгва без склад; няма кого да питаме
  const guard = (warehouseId: number | null, proceed: () => void) => {
    const colleague =
      warehouseId != null ? (unfinishedOf(warehouseId).find((entry) => !entry.mine)?.draft ?? null) : null;
    if (!colleague) {
      proceed();
      return;
    }
    setAsking({ draft: colleague, warehouseName: warehouseNameOf(warehouseId!), proceed });
  };

  const draft = asking?.draft ?? null;
  const name = draft?.ownerName || "?";
  const first = name.split(/\s+/)[0];
  const modal = (
    <ConfirmDialog
      open={asking != null}
      intent="warning"
      icon={<TeamOutlined />}
      title={t("inventoryPage.stkColleagueAskTitle", { name, warehouse: asking?.warehouseName ?? "" })}
      explanation={t("inventoryPage.stkColleagueAskText")}
      preview={
        draft
          ? {
              caption: t("inventoryPage.stkColleagueAskCaption"),
              items: [
                {
                  key: draft.key,
                  label: draft.label || asking?.warehouseName || "",
                  hint: draft.updatedAt
                    ? t("inventoryPage.homeDraftUpdated", { when: dayjs(draft.updatedAt).format("DD.MM HH:mm") })
                    : undefined,
                  value: t("inventoryPage.stocktakeCountedShort", { count: draft.linesCount }),
                },
              ],
            }
          : undefined
      }
      choices={[
        {
          key: "continue",
          label: t("inventoryPage.stkColleagueContinue", { first }),
          hint: t("inventoryPage.stkColleagueContinueHint"),
          primary: true,
          onSelect: () => {
            setAsking(null);
            draft?.onOpen();
          },
        },
        {
          key: "separate",
          label: t("inventoryPage.stkColleagueSeparate"),
          hint: t("inventoryPage.stkColleagueSeparateHint", {
            warehouse: asking?.warehouseName ?? "",
            first,
          }),
          onSelect: () => {
            const proceed = asking?.proceed;
            setAsking(null);
            proceed?.();
          },
        },
      ]}
      onCancel={() => setAsking(null)}
    />
  );

  return { guard, modal };
};
